"use client";

import { useState, useEffect } from "react";
import { AuthorAvatar, ShareButton } from "@/components/ui/";
import CodeBlock from "@/components/ui/code-block";
import { AlignJustify } from "@/components/icons";
import { TOC_ITEMS } from "./data";

export function KeyFindings() {
  return (
    <div
      className="my-10 overflow-hidden rounded-2xl border"
      style={{
        borderColor: "color-mix(in srgb, var(--foreground) 5%, transparent)",
        background:
          "linear-gradient(135deg, var(--surface-1) 0%, color-mix(in srgb, var(--surface-1) 60%, var(--background)) 100%)",
      }}
    >
      <div className="divide-foreground/6 grid divide-y sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        {[
          { value: "99.4%", label: "Mobile proxy success rate", sub: "vs. 87.2% for residential" },
          { value: "34×", label: "Lower IP block rate", sub: "with per-request rotation" },
        ].map((stat) => (
          <div key={stat.value} className="flex flex-col gap-1 px-8 py-7">
            <span className="text-muted-foreground text-4xl font-black tracking-tight">
              {stat.value}
            </span>
            <span className="text-foreground text-sm font-semibold">{stat.label}</span>
            <span className="text-muted-foreground text-xs">{stat.sub}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ResearchChart() {
  const bars = [
    { label: "Mobile", value: 99.4, color: "#f59e0b" },
    { label: "Residential", value: 87.2, color: "#06b6d4" },
    { label: "Datacenter", value: 62.1, color: "#22d3ee" },
    { label: "ISP", value: 81.5, color: "#5b8cff" },
  ];
  const chartH = 120;

  return (
    <div
      className="my-10 overflow-hidden rounded-2xl border"
      style={{
        borderColor: "color-mix(in srgb, var(--foreground) 5%, transparent)",
        background: "color-mix(in srgb, var(--surface-1) 43%, var(--background))",
      }}
    >
      <div className="px-6 pt-6 pb-2">
        <p className="text-muted-foreground mb-1 text-xs font-medium tracking-widest uppercase">
          Success Rate by Proxy Type
        </p>
        <p className="text-foreground text-base font-semibold">
          Mobile proxies lead with a 99.4% average success rate across all target tiers
        </p>
      </div>
      <div className="px-6 pt-4 pb-4">
        <svg
          viewBox={`0 0 ${bars.length * 100} ${chartH + 40}`}
          className="w-full"
          style={{ height: 180 }}
          aria-label="Bar chart showing success rates by proxy type"
        >
          <defs>
            <pattern
              id="rc-grid"
              width={bars.length * 100}
              height="24"
              patternUnits="userSpaceOnUse"
            >
              <line
                x1="0"
                y1="0"
                x2={bars.length * 100}
                y2="0"
                stroke="var(--foreground)"
                strokeWidth="0.4"
                strokeOpacity="0.05"
              />
            </pattern>
          </defs>
          <rect x="0" y="0" width={bars.length * 100} height={chartH} fill="url(#rc-grid)" />
          {bars.map((bar, i) => {
            const barH = (bar.value / 100) * chartH;
            const x = i * 100 + 20;
            const y = chartH - barH;
            return (
              <g key={bar.label}>
                <rect x={x} y={y} width="60" height={barH} fill={bar.color} opacity="0.15" rx="4" />
                <rect
                  x={x}
                  y={y + barH * 0.2}
                  width="60"
                  height={barH * 0.8}
                  fill={bar.color}
                  opacity="0.55"
                  rx="4"
                />
                <text
                  x={x + 30}
                  y={y - 6}
                  textAnchor="middle"
                  fontSize="10"
                  fontWeight="700"
                  fill={bar.color}
                >
                  {bar.value}%
                </text>
                <text
                  x={x + 30}
                  y={chartH + 16}
                  textAnchor="middle"
                  fontSize="9"
                  fill="color-mix(in srgb, var(--foreground) 40%, transparent)"
                >
                  {bar.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      <div className="px-6 pb-5">
        <p className="text-muted-foreground text-xs italic">
          Fig 1. Average success rate across 500+ targets over 90 days (n=300,000 requests per proxy
          type). Tier-3 anti-bot targets only.
        </p>
      </div>
    </div>
  );
}

export { CodeBlock, AuthorAvatar };

export function TableOfContents() {
  const [activeId, setActiveId] = useState<string>(TOC_ITEMS[0].id);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    TOC_ITEMS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveId(id);
        },
        { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <aside
      className="sticky top-24 hidden w-52 shrink-0 self-start xl:block"
      aria-label="Table of contents"
    >
      <div className="mb-5 flex items-center gap-2.5">
        <AlignJustify size={15} className="text-muted-foreground shrink-0" aria-hidden="true" />
        <span className="text-muted-foreground text-sm font-medium">On this page</span>
      </div>
      <nav>
        <ol className="flex flex-col">
          {TOC_ITEMS.map((item) => {
            const isActive = activeId === item.id;
            return (
              <li key={item.id} className="relative">
                <span
                  className="absolute top-0 bottom-0 left-0 w-px"
                  style={{ background: isActive ? "var(--foreground)" : "var(--border)" }}
                  aria-hidden="true"
                />
                <a
                  href={`#${item.id}`}
                  onClick={() => setActiveId(item.id)}
                  className="block py-2 pl-4 text-sm leading-snug transition-colors duration-150"
                  style={{
                    color: isActive ? "var(--foreground)" : "var(--muted-foreground)",
                    fontWeight: isActive ? 600 : 400,
                  }}
                >
                  {item.label}
                </a>
              </li>
            );
          })}
        </ol>
      </nav>
    </aside>
  );
}

export { ShareButton };
